import { formatCurrency } from '@westhetics/ui'
import type { Period, PeriodMetrics, SeriesPoint } from './api'

/** Texto do dashboard (docs/areas/dashboard.md; docs/styleguide/04-escrita.md). */

export const PERIOD_LABELS: Record<Period, string> = {
  week: 'Semana',
  month: 'Mês',
  quarter: 'Trimestre',
}

export const PERIOD_RANGE_LABELS: Record<Period, string> = {
  week: 'últimos 7 dias',
  month: 'últimos 30 dias',
  quarter: 'últimos 90 dias',
}

/** "3 coisas pedem atenção hoje" · "1 coisa pede atenção hoje" */
export function pendingTitle(total: number) {
  return total === 1 ? '1 coisa pede atenção hoje' : `${total} coisas pedem atenção hoje`
}

/** "ver todas as 7 pendências" */
export function seeAllLabel(total: number) {
  return `ver todas as ${total} pendências`
}

/** "Quarta, 14 de outubro": a data por extenso do cabeçalho (RN-DSH-21). */
export function dateHeading(date: Date) {
  const weekday = new Intl.DateTimeFormat('pt-BR', { weekday: 'long' })
    .format(date)
    .replace('-feira', '')
  const day = String(date.getDate()).padStart(2, '0')
  const month = new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(date)
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${day} de ${month}`
}

/** "R$ 3.166" (sem centavos quando inteiro) para a linha do número-herói. */
export function money(cents: number) {
  return formatCurrency(cents / 100, { compact: cents % 100 === 0 })
}

/** "+12% vs. período anterior" · "novo no período" · "igual ao período anterior" */
export function deltaLabel(current: number, previous: number) {
  if (previous === 0 && current === 0) return 'igual ao período anterior'
  if (previous === 0) return 'novo no período'
  const pct = Math.round(((current - previous) / previous) * 100)
  if (pct === 0) return 'igual ao período anterior'
  return `${pct > 0 ? '+' : '−'}${Math.abs(pct)}% vs. período anterior`
}

/** Os números que a evolução sabe desenhar; a chave é a do ponto da série. */
export type MetricKey =
  | 'generatedCents'
  | 'generatedCount'
  | 'netRevenueCents'
  | 'newPatients'
  | 'anamnesesCompleted'
  | 'documentsGenerated'

export interface Metric {
  key: MetricKey
  label: string
  format: (value: number) => string
  /** O total do período para este número. */
  total: (m: PeriodMetrics) => number
}

const count = (value: number) => String(value)

export const METRICS: Metric[] = [
  {
    key: 'generatedCents',
    label: 'Valor gerado',
    format: money,
    total: (m) => m.generated.amountCents,
  },
  { key: 'generatedCount', label: 'Atendimentos', format: count, total: (m) => m.generated.count },
  {
    key: 'newPatients',
    label: 'Pacientes novos',
    format: count,
    total: (m) => m.movement.newPatients,
  },
  {
    key: 'anamnesesCompleted',
    label: 'Anamneses',
    format: count,
    total: (m) => m.movement.anamnesesCompleted,
  },
  {
    key: 'documentsGenerated',
    label: 'Documentos',
    format: count,
    total: (m) => m.movement.documentsGenerated,
  },
  {
    key: 'netRevenueCents',
    label: 'Faturamento real',
    format: money,
    total: (m) => m.netRevenueCents,
  },
]

/** "Valor gerado por dia" · "Atendimentos por semana" */
export function chartLabel(metric: Metric, bucket: 'day' | 'week') {
  return `${metric.label} por ${bucket === 'day' ? 'dia' : 'semana'}`
}

export function pointValue(point: SeriesPoint, key: MetricKey) {
  return point[key]
}

/** "3 atendimentos gerados · 3 pacientes" */
export function generatedLine(m: PeriodMetrics) {
  const appointments =
    m.generated.count === 1 ? '1 atendimento gerado' : `${m.generated.count} atendimentos gerados`
  const patients = m.generated.patients === 1 ? '1 paciente' : `${m.generated.patients} pacientes`
  return `${appointments} · ${patients}`
}

/** "9 pacientes · ticket médio R$ 3.166 · idade média 33" (RN-DSH-04). */
export function billedLine(m: PeriodMetrics) {
  const parts = [
    m.patientsServed === 1 ? '1 paciente' : `${m.patientsServed} pacientes`,
    `ticket médio ${money(m.averageAppointmentValueCents)}`,
  ]
  if (m.averageAge !== null) parts.push(`idade média ${m.averageAge}`)
  return parts.join(' · ')
}

export const SIGNATURES_NOTE = 'Assinatura e faturamento real chegam com a área de Assinaturas.'
export const MOBILE_NOTE =
  'Faturamento não aparece aqui: no celular a tela inicial é a operação do dia.'
export const FOOTNOTE = 'Faturamento real é bruto menos ressarcimentos.'
